import renderAsAllDay from 'owa-calendar-types/lib/utils/renderAsAllDay';
import resolveAllDayColumnsWithItems from './resolveAllDayColumnsWithItems';
import resolveRowsWithItems from './resolveRowsWithItems';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import compareCalendarEvents from 'owa-calendar-types/lib/utils/compareCalendarEvents';
import CalendarSurfaceStore from '../store/schema/CalendarSurfaceStore';
import { ColumnProvider } from '../interfaces/ColumnProvider';
import { computed } from 'mobx';
import { createSurfaceItem } from './createSurfaceItem';
import { FreeBusyConstants } from 'owa-freebusy-style';
import { groupByStartColumn } from './groupByStartColumn';
import { LayoutInterval } from '../interfaces/LayoutInterval';
import { merge } from './merge';
import { SurfaceItem } from '../interfaces/SurfaceItem';
import { SurfaceConstants } from 'owa-calendar-surface-core';
import { default as getOverflowIndicatorId } from '../utils/getOverflowIndicatorId';

/**
 * This interface defines a data contract for day/week content generation
 */
export interface ColumnItemsProvider {
    inDayItems: SurfaceItem[];
    columnInDayItems: SurfaceItem[][];
    allDayRows: SurfaceItem[][];
    allDayUncollapsedRows: SurfaceItem[][];
    allDayHiddenItems: SurfaceItem[];
    busyStripModels: SurfaceItem[][];
    maxRows: number;
    tabOrder: string[];
    visibleRowCount: number;
}

/**
 * Implements data provider for Day/Week content generation
 */
export class SurfaceColumnItemsProvider implements ColumnItemsProvider {
    private maxCollapsedAllDayRowsToShow: number;
    private allDayWellHeight: number;

    constructor(
        private store: CalendarSurfaceStore,
        private columnProvider: ColumnProvider,
        private getItems: () => CalendarEvent[],
        private supportExpandableAllDayWell: boolean,
        maxCollapsedAllDayRowsToShow?: number,
        private customEventsComparator?: (item1: CalendarEvent, item2: CalendarEvent) => number,
        allDayWellHeight?: number
    ) {
        this.maxCollapsedAllDayRowsToShow = maxCollapsedAllDayRowsToShow;
        this.allDayWellHeight = allDayWellHeight;
    }

    /**
     * Gets in-day surface items grouped by columns
     */
    @computed
    get columnInDayItems(): SurfaceItem[][] {
        const columnCount = this.columnProvider.columnCount;
        const columnLength = this.store.dayLength;
        return groupByStartColumn(this.inDayItems, columnCount, columnLength);
    }

    /**
     * Gets all-day surface items grouped by columns
     */
    @computed
    private get columnAllDayItems(): SurfaceItem[][] {
        const columnCount = this.columnProvider.columnCount;
        const columnLength = this.store.dayLength;
        return groupByStartColumn(this.allDayItems, columnCount, columnLength);
    }

    /**
     * Gets all day surface items grouped by rows
     */
    @computed
    get allDayUncollapsedRows(): SurfaceItem[][] {
        const columnCount = this.columnProvider.columnCount;
        const columnLength = this.store.dayLength;
        return resolveRowsWithItems(this.allDayItems, columnCount, columnLength).VisibleRows;
    }

    /**
     * Gets all day surface items grouped by visible rows
     */
    @computed
    get allDayRows(): SurfaceItem[][] {
        const columnCount = this.columnProvider.columnCount;
        const columnLength = this.store.dayLength;
        const maxRows =
            this.store.isAllDayWellExpanded && this.supportExpandableAllDayWell
                ? undefined // when expanded, no maximum, it shows all items
                : this.maxRows - 1; // subtract 1 for the hidden items
        return resolveRowsWithItems(this.allDayItems, columnCount, columnLength, maxRows)
            .VisibleRows;
    }

    /**
     * Gets hidden all day surface items
     */
    @computed
    get allDayHiddenItems(): SurfaceItem[] {
        if (!this.supportExpandableAllDayWell || this.store.isAllDayWellExpanded) {
            return [];
        }

        const columnCount = this.columnProvider.columnCount;
        const columnLength = this.store.dayLength;
        const maxRows = this.maxRows - 1; // subtract 1 for the hidden items
        return resolveRowsWithItems(this.allDayItems, columnCount, columnLength, maxRows)
            .HiddenItems;
    }

    /**
     * Gets number of maximum rows
     */
    @computed
    get maxRows(): number {
        if (this.maxCollapsedAllDayRowsToShow) {
            // Use scenario specified maxCollapsedAllDayRowsToShow if it's present
            return this.maxCollapsedAllDayRowsToShow;
        }

        if (!this.supportExpandableAllDayWell) {
            // if scenario doesn't support expandable allDayWell, don't limit the row count
            return null;
        }

        if (this.store.isAllDayWellExpanded) {
            return this.allDayUncollapsedRows.length;
        }

        return this.allDayWellHeight
            ? Math.floor(this.allDayWellHeight / SurfaceConstants.AllDayRowHeight)
            : SurfaceConstants.defaultAllDayWellCollapsedMaxRows;
    }

    /**
     * Gets number of total visible rows
     */
    @computed
    get visibleRowCount(): number {
        if (this.allDayHiddenItems.length > 0) {
            return this.allDayRows.length + 1;
        }
        return this.allDayRows.length;
    }

    /**
     * Gets all days surface items
     */
    @computed
    get allDayItems(): SurfaceItem[] {
        let results: SurfaceItem[] = [];
        this.items
            .filter(itemModel => renderAsAllDay(itemModel))
            .forEach(model => {
                let intervals: LayoutInterval[] = this.columnProvider.getColumnIntervals(model);
                if (intervals.length > 0) {
                    // create item only if in range.
                    results.push(createSurfaceItem(merge(intervals), model.ItemId.Id, model));
                }
            });
        return results;
    }

    /**
     * Gets in-day surface items
     */
    @computed
    get inDayItems(): SurfaceItem[] {
        let results: SurfaceItem[] = [];
        this.items
            .filter(itemModel => !renderAsAllDay(itemModel))
            .forEach(model => {
                let intervals: LayoutInterval[] = this.columnProvider.getColumnIntervals(model);
                results.push(
                    ...intervals.map<SurfaceItem>((inst, i) => {
                        return {
                            ...inst,
                            ...{ key: model.ItemId.Id + '_' + i, model: model },
                        };
                    })
                );
            });
        return results;
    }

    /**
     * Gets tab order
     * Tab order is grouped by column.
     * Colunmn group starts with header, then followed by all day events and then by in-day events
     */
    @computed
    get tabOrder(): string[] {
        let results: string[] = [];
        this.columnProvider.columnModels.forEach((model, index) => {
            // push just the number for the id of the column header
            results.push('surfaceHeader_' + index);

            // push all the column data
            const overflowItems =
                this.columnAllDayItems[index].length > this.visibleRowCount
                    ? [getOverflowIndicatorId(model)]
                    : [];

            // flatten the array, not all browsers supprt .flat()
            const flatAllDayRows = [].concat.apply([], this.allDayRows);

            const visibleAllDayItems = this.columnAllDayItems[index].filter(i =>
                flatAllDayRows.includes(i)
            );
            const invisibleAllDayItems = this.columnAllDayItems[index].filter(
                i => !visibleAllDayItems.includes(i)
            );

            results.push(...visibleAllDayItems.map(item => item.key));
            results.push(...overflowItems);
            results.push(...invisibleAllDayItems.map(item => item.key));
            results.push(...this.columnInDayItems[index].map(item => item.key));
        });
        return results;
    }

    /**
     * Gets busy strip models for in-day view
     */
    @computed
    get busyStripModels(): SurfaceItem[][] {
        const modelsWithFreeBusyInfo = this.items.filter(
            model =>
                model.FreeBusyType != null &&
                (model.FreeBusyType == FreeBusyConstants.Busy ||
                    model.FreeBusyType == FreeBusyConstants.OOF) &&
                renderAsAllDay(model)
        );

        return resolveAllDayColumnsWithItems(
            modelsWithFreeBusyInfo,
            this.store,
            this.columnProvider
        );
    }

    /**
     * Gets sorted collection of all item models.
     * Filters out items that are completely oot-of-range of current view
     */
    @computed
    get sortedItems(): CalendarEvent[] {
        const comparator = this.customEventsComparator
            ? this.customEventsComparator
            : compareCalendarEvents;
        const allItems = this.getItems()
            .filter(model => this.columnProvider.getColumnIntervals(model).length > 0)
            .sort(comparator);

        return allItems;
    }

    /**
     * Gets sorted collection of all item models with the colorProvider applied to them
     */
    @computed
    private get items(): CalendarEvent[] {
        return this.sortedItems;
    }
}
